import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import TextInput from 'components/ui-components/TextInput';
import { PostcodeSelectorContext } from '../PostcodeSelectorProvider';
import {
  populateAddressFields,
  setField,
  setSubmittedAddress,
} from '../postcodeSelectorStateManager';
import { validateRequired, validatePostcode } from '../validateFields';
import {
  addressHouseNumber,
  addressLine1Required,
  addressLine4Required,
} from '../errorMessages';

const AddressFields = ({ addressFieldsState }) => {
  const {
    id,
    dispatch,
    selectedAddress,
    setValue,
    errors,
    setError,
    clearErrors,
  } = useContext(PostcodeSelectorContext);

  const {
    [`${id}Number`]: number,
    [`${id}Line1`]: line1,
    [`${id}Line2`]: line2,
    [`${id}Line3`]: line3,
    [`${id}Line4`]: line4,
    [`${id}Postcode`]: postcode,
  } = addressFieldsState;

  const requiredFields = [
    { value: number, field: 'Number', errorMessage: addressHouseNumber },
    { value: line1, field: 'Line1', errorMessage: addressLine1Required },
    { value: line4, field: 'Line4', errorMessage: addressLine4Required },
  ];

  useEffect(() => {
    if (selectedAddress) {
      dispatch(populateAddressFields(selectedAddress));
    }
  }, [selectedAddress]);

  // When fields updated via the dropdown population or if the user edits the
  // field directly, set the submitted address
  useEffect(() => {
    // Once users details have been retrieved check required fields have data
    if (postcode) {
      requiredFields.forEach(({ value, field, errorMessage }) => {
        validateRequired(value, {
          inputField: `${id}${field}`,
          validationField: `${id}${field}Valid`,
          errorMessage,
          setValue,
          setError,
          clearErrors,
        });
      });
    }

    dispatch(
      setSubmittedAddress({
        number,
        line1,
        line2,
        line3,
        line4,
        postcode,
      }),
    );
  }, [number, line1, line2, line3, line4, postcode]);

  const setFieldValue = field => event => {
    dispatch(setField(field, event.target.value));
  };

  const handleValidateRequired = (
    value,
    inputField,
    validationField,
    errorMessage,
  ) => {
    validateRequired(value, {
      inputField,
      validationField,
      errorMessage,
      setValue,
      setError,
      clearErrors,
    });

    // Set field value
    dispatch(setField(inputField, value));
  };

  const handleValidatePostcode = (value, inputField, validationField) => {
    validatePostcode(value, {
      inputField,
      validationField,
      setValue,
      setError,
      clearErrors,
    });

    // Set field value
    dispatch(setField(inputField, value));
  };

  return (
    <div className="mt-30">
      <TextInput
        id={`${id}Number`}
        label="House Number/Name"
        value={number}
        onChange={event =>
          handleValidateRequired(
            event.target.value,
            `${id}Number`,
            `${id}NumberValid`,
            addressHouseNumber,
          )
        }
        errors={errors && errors[`${id}Number`] ? errors : null}
      />

      <TextInput
        className="mt-20"
        id={`${id}Line1`}
        label="Address Line 1"
        value={line1}
        onChange={event =>
          handleValidateRequired(
            event.target.value,
            `${id}Line1`,
            `${id}Line1Valid`,
            addressLine1Required,
          )
        }
        errors={errors && errors[`${id}Line1`] ? errors : null}
      />

      <TextInput
        className="mt-20"
        id={`${id}Line2`}
        label="Address Line 2 <span>(Optional)</span>"
        value={line2}
        onChange={setFieldValue(`${id}Line2`)}
      />

      <TextInput
        className="mt-20"
        id={`${id}Line3`}
        label="County <span>(Optional)</span>"
        value={line3}
        onChange={setFieldValue(`${id}Line3`)}
      />

      <TextInput
        className="mt-20"
        id={`${id}Line4`}
        label="Town"
        value={line4}
        onChange={event =>
          handleValidateRequired(
            event.target.value,
            `${id}Line4`,
            `${id}Line4Valid`,
            addressLine4Required,
          )
        }
        errors={errors && errors[`${id}Line4`] ? errors : null}
      />

      <TextInput
        className="mt-20 m-width-40"
        id={`${id}Postcode`}
        label="Postcode"
        value={postcode}
        onChange={event =>
          handleValidatePostcode(
            event.target.value,
            `${id}Postcode`,
            `${id}PostcodeValid`,
          )
        }
        errors={errors && errors[`${id}Postcode`] ? errors : null}
      />
    </div>
  );
};

AddressFields.propTypes = {
  addressFieldsState: PropTypes.object.isRequired,
};

export default AddressFields;
